import React from 'react';
import clsx from 'clsx';
import {
  Draggable,
  Droppable,
} from '@hello-pangea/dnd';
import {
  useFormContext,
} from 'react-hook-form';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import map from 'lodash/map';
import compact from 'lodash/compact';
import {
  InsuranceDTO,
  VisitNoteBillingCodeDTO,
} from 'dtos';
import Controller from 'core/components/FormController';
import DeleteIcon from 'core/icons/delete';
import ToolTip from 'core/components/Tooltip';
import Button from 'pages/Dashboard/components/Button';
import {
  FieldComponentProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import Chip from 'core/components/Chip';

export default function SplitItem({
  index,
  isLast,
  prefix,
  value,
  patient,
  append,
  onDelete,
  update,
}: FieldComponentProps) {
  const {
    control,
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const splits = watch('splitClaims');
  const key = `${prefix}.${index}`;

  return (
    <>
      <div className="flex flex-col gap-2 w-[calc(25%-12px)] h-full pr-3">
        <div className="flex items-center justify-between h-6">
          <h2 className="font-semibold">
            Split
            {' '}
            {index + 1}
          </h2>
          {(splits.length > 1 && /^new-claim__/i.test(value?.claimId)) && (
            <ToolTip title="Remove Claim Split">
              <IconButton
                onClick={() => {
                  const moveToIndex = Number(index === 0);
                  const neighborsKey = `splitClaims.${moveToIndex}`;
                  const neighborsClaim = getValues(neighborsKey);
                  const neighborsClaimId = neighborsClaim.claimId;
                  setValue(`${neighborsKey}.serviceCodes`, [
                    ...(neighborsClaim?.serviceCodes ?? []),
                    ...(value.serviceCodes ?? []).map((service: VisitNoteBillingCodeDTO) => ({
                      ...service,
                      serviceCodeDetails: {
                        ...(service?.serviceCodeDetails ?? {}),
                        claimId: neighborsClaimId,
                        primaryClaimId: neighborsClaimId,
                      },
                    })),
                  ]);
                  onDelete(index);
                }}
                size="small"
                color="error"
                className="relative top-0.5"
              >
                <DeleteIcon className="!w-[18px] !h-[18px] !text-gray-700" />
              </IconButton>
            </ToolTip>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-1">
          <Controller
            control={control}
            name={`${key}.insuranceId`}
            render={({ field }) => (
              <PatientInsuranceSelect
                {...field}
                classes={{ rootContainer: '!flex-none !items-start ' }}
                patientId={patient?.patientId ?? 0}
                insuranceId={field.value}
                editable={false}
                onChange={(insurance: InsuranceDTO | InsuranceDTO[]) => {
                  field.onChange((insurance as InsuranceDTO)?.insuranceId);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name={`${key}.serviceCodes`}
            render={({ field }) => (

              <div className={clsx(
                'relative flex-1 !w-full !mt-0',
                { 'h-full flex items-center justify-center text-center': field.value?.length === 0 },
              )}
              >
                <Droppable droppableId={value.claimId}>
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={clsx(
                        'h-full w-full border border-dotted border-gray-400 rounded',
                        {
                          'flex flex-col items-center justify-center': field.value?.length === 0,
                          'p-2': (field?.value ?? []).length > 0,
                        },
                      )}
                    >
                      {field.value?.length === 0
                        ? (
                          <span className="text-xs">No Service Codes Yet.</span>
                        )
                        : (field.value as VisitNoteBillingCodeDTO[]).map(
                          (item, codeIndex: number) => (
                            <Draggable
                              key={item.visitNoteBillingCodeId}
                              draggableId={item?.visitNoteBillingCodeId?.toString() ?? ''}
                              index={codeIndex}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    marginTop: '10px',
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <Chip
                                    draggable
                                    classes={{ chip: '!w-full' }}
                                    value={compact([
                                      item?.code ?? '',
                                      map(item?.modifiers, 'code').join(', '),
                                    ]).join(': ')}
                                    onClose={() => {
                                      if (typeof update === 'function') {
                                        update(index, {
                                          ...value,
                                          serviceCodes: [
                                            ...value.serviceCodes.slice(0, codeIndex),
                                            ...value.serviceCodes.slice(codeIndex + 1),
                                          ],
                                        });
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ),
                        )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}
          />
        </div>
      </div>
      {isLast && (
        <Droppable droppableId={`new-claim__${index + 1}`}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="h-full w-[calc(25%-12px)]"
            >
              <Button
                color="primary"
                variant="text"
                className="!text-xs !font-medium flex items-center justify-center h-full !min-w-full !border !border-blue-500 !border-dotted"
                onClick={append}
              >
                <AddIcon
                  className="mr-1"
                  fontSize="small"
                />
                Add
              </Button>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </>
  );
}
